import {
  Color3,
  Color4,
  DynamicTexture,
  Effect,
  Mesh,
  MeshBuilder,
  Scene,
  ShaderMaterial,
  StandardMaterial,
  TransformNode,
  Vector3,
  type Camera,
} from '@babylonjs/core';

/**
 * Stylized sky: a gradient skybox + a handful of billboarded cloud planes.
 *
 * The gradient is a custom ShaderMaterial on a large CreateBox. Clouds are 8–12
 * billboarded planes parented to a TransformNode that follows the camera laterally,
 * so they're always present regardless of where the player drives.
 *
 * The horizon color is shared with `scene.fogColor` and `scene.clearColor` so
 * unloaded chunk seams blend invisibly into the sky.
 */

export interface SkyOptions {
  /** RGB 0..1 — top of the sky. */
  zenith: [number, number, number];
  /** RGB 0..1 — middle band of the sky. */
  mid: [number, number, number];
  /** RGB 0..1 — horizon (matches fog + clear color). */
  horizon: [number, number, number];
  /** Cloud drift velocity in meters/sec (world XZ). */
  windX: number;
  windZ: number;
  /** Cloud layer altitude in world Y. */
  cloudAltitude: number;
  /** Number of cloud sprites. */
  cloudCount: number;
}

export const DEFAULT_SKY_OPTIONS: SkyOptions = {
  // Bright cartoon cyan: saturated zenith → soft cyan mid → near-white horizon.
  zenith: [0.239, 0.659, 0.910], // #3DA8E8
  mid: [0.624, 0.839, 0.949], // #9FD6F2
  horizon: [0.831, 0.937, 0.973], // #D4EFF8
  windX: 4.0,
  windZ: 1.5,
  cloudAltitude: 600,
  cloudCount: 10,
};

export interface SkyHandle {
  /** The skybox horizon color — apply this to scene.fogColor / scene.clearColor. */
  horizonColor: Color3;
  dispose(): void;
}

export function createSky(scene: Scene, camera: Camera, options: Partial<SkyOptions> = {}): SkyHandle {
  const opt: SkyOptions = { ...DEFAULT_SKY_OPTIONS, ...options };

  registerSkyShader();

  const skybox = MeshBuilder.CreateBox('skybox', { size: 2000 }, scene);
  skybox.infiniteDistance = true;
  skybox.isPickable = false;
  skybox.applyFog = false;

  const sky = new ShaderMaterial(
    'sky-mat',
    scene,
    { vertex: 'roadworldSky', fragment: 'roadworldSky' },
    {
      attributes: ['position'],
      uniforms: ['world', 'worldViewProjection', 'zenith', 'mid', 'horizon'],
    },
  );
  sky.backFaceCulling = false;
  sky.disableDepthWrite = true;
  sky.setColor3('zenith', new Color3(...opt.zenith));
  sky.setColor3('mid', new Color3(...opt.mid));
  sky.setColor3('horizon', new Color3(...opt.horizon));
  skybox.material = sky;
  skybox.renderingGroupId = 0;

  // Clouds — parented to a TransformNode that tracks the camera in XZ so the
  // cloud layer is always overhead. Drift slowly via a wind vector.
  const cloudRoot = new TransformNode('cloud-root', scene);
  const cloudTexture = makeCloudTexture(scene);
  const cloudMat = new StandardMaterial('cloud-mat', scene);
  cloudMat.diffuseTexture = cloudTexture;
  cloudMat.diffuseTexture.hasAlpha = true;
  cloudMat.useAlphaFromDiffuseTexture = true;
  cloudMat.emissiveColor = new Color3(1, 1, 1);
  cloudMat.disableLighting = true;
  cloudMat.disableDepthWrite = true;
  cloudMat.backFaceCulling = false;
  const clouds: Mesh[] = [];
  for (let i = 0; i < opt.cloudCount; i++) {
    const c = MeshBuilder.CreatePlane(`cloud-${i}`, { size: 220 + (i % 4) * 80 }, scene);
    c.parent = cloudRoot;
    const angle = (i / opt.cloudCount) * Math.PI * 2;
    const r = 350 + ((i * 53) % 220);
    c.position.set(Math.cos(angle) * r, opt.cloudAltitude + ((i * 31) % 80), Math.sin(angle) * r);
    c.billboardMode = Mesh.BILLBOARDMODE_Y;
    c.applyFog = false;
    c.isPickable = false;
    c.renderingGroupId = 0;
    c.material = cloudMat;
    clouds.push(c);
  }

  const drift = new Vector3(0, 0, 0);
  const observer = scene.onBeforeRenderObservable.add(() => {
    const dt = scene.getEngine().getDeltaTime() / 1000;
    drift.x += opt.windX * dt;
    drift.z += opt.windZ * dt;
    const cam = camera.position;
    cloudRoot.position.set(cam.x + (drift.x % 800) - 400, 0, cam.z + (drift.z % 800) - 400);
  });

  scene.clearColor = new Color4(opt.horizon[0], opt.horizon[1], opt.horizon[2], 1);

  return {
    horizonColor: new Color3(...opt.horizon),
    dispose() {
      scene.onBeforeRenderObservable.remove(observer);
      for (const c of clouds) c.dispose();
      cloudMat.dispose();
      cloudTexture.dispose();
      sky.dispose();
      skybox.dispose();
      cloudRoot.dispose();
    },
  };
}

/** Build a soft radial cloud sprite once and reuse for every cloud plane. */
function makeCloudTexture(scene: Scene): DynamicTexture {
  const size = 256;
  const tex = new DynamicTexture('cloud-tex', { width: size, height: size }, scene, false);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  ctx.clearRect(0, 0, size, size);

  // Soft radial gradient + a few jittered blobs.
  function blob(cx: number, cy: number, r: number, alpha: number): void {
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    g.addColorStop(0, `rgba(255,255,255,${alpha})`);
    g.addColorStop(0.6, `rgba(255,255,255,${alpha * 0.4})`);
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
  }
  blob(size * 0.5, size * 0.55, size * 0.45, 0.95);
  blob(size * 0.35, size * 0.5, size * 0.3, 0.7);
  blob(size * 0.65, size * 0.45, size * 0.28, 0.7);
  blob(size * 0.7, size * 0.6, size * 0.22, 0.5);
  blob(size * 0.4, size * 0.7, size * 0.2, 0.5);

  tex.hasAlpha = true;
  tex.update(false);
  return tex;
}

let skyShaderRegistered = false;

function registerSkyShader(): void {
  if (skyShaderRegistered) return;
  skyShaderRegistered = true;

  Effect.ShadersStore['roadworldSkyVertexShader'] = `
    precision highp float;
    attribute vec3 position;
    uniform mat4 worldViewProjection;
    varying vec3 vDir;
    void main(void) {
      vDir = normalize(position);
      // Skybox "z = w" trick: force NDC z to 1.0 (the far plane) by setting clip-space
      // z equal to clip-space w. Without this, the cube is at finite distance (~size/2
      // from camera) and any geometry past that radius loses the LEQUAL depth test and
      // gets occluded by the sky — silhouettes of distant terrain disappear on the
      // horizon. Using a vec4 constructor instead of an assignment to .z keeps the
      // shader compatible with Babylon's WGSL auto-translation for WebGPU.
      vec4 p = worldViewProjection * vec4(position, 1.0);
      gl_Position = vec4(p.x, p.y, p.w, p.w);
    }
  `;

  Effect.ShadersStore['roadworldSkyFragmentShader'] = `
    precision highp float;
    varying vec3 vDir;
    uniform vec3 zenith;
    uniform vec3 mid;
    uniform vec3 horizon;
    void main(void) {
      // Map vDir.y from [-1,1] to a smooth gradient zenith -> mid -> horizon.
      float h = clamp(vDir.y, -0.2, 1.0);
      // Below the horizon, just use horizon color (no ground).
      float toMid = smoothstep(0.0, 0.35, h);
      float toZenith = smoothstep(0.25, 0.85, h);
      vec3 col = mix(horizon, mid, toMid);
      col = mix(col, zenith, toZenith);
      gl_FragColor = vec4(col, 1.0);
    }
  `;
}
